
import { NavLink, useLocation } from "react-router-dom";
import { TrendingUp, Coins, Package, ArrowRight, Gem, Calculator, BookOpen } from "lucide-react";
import {
  NavigationMenu,
  NavigationMenuContent,
  NavigationMenuItem,
  NavigationMenuLink,
  NavigationMenuList,
  NavigationMenuTrigger,
} from "@/components/ui/navigation-menu";
import { cn } from "@/lib/utils";
import { useHydrationSafe } from "@/hooks/useHydrationSafe";

interface NavItemsProps {
  isAuthenticated: boolean;
  userRole: string | null | undefined;
  className?: string;
  onItemClick?: () => void;
}

const oroRoutes = ["/oro-da-investimento", "/monete-oro-investimento", "/lingotti-oro-investimento"];
const diamantiRoutes = ["/diamanti", "/quotazione-diamanti", "/calcolatore-carati-diamante", "/le-4c-del-diamante"];

const navLinkClass = ({ isActive }: { isActive: boolean }) =>
  cn("text-sm font-medium transition-colors", isActive ? "text-gold" : "hover:text-gold/80");

/**
 * SSR-Safe static navigation links
 * Renders during SSR and initial hydration to ensure HTML parity
 * 
 * CRITICAL: Uses plain <a> tags instead of NavLink to prevent hydration mismatch #418
 * - NavLink generates dynamic aria-current and className based on isActive
 * - SSR uses StaticRouter (req.originalUrl) vs client uses BrowserRouter (window.location)
 * - Minor URL differences (trailing slash, query params) cause isActive to differ → mismatch
 * - Plain <a> tags produce 100% deterministic output regardless of router context
 */
function StaticNavLinks({ className }: { className?: string }) {
  // Static class - no isActive logic, no dynamic attributes
  const staticClass = "text-sm font-medium transition-colors hover:text-gold/80";
  
  return (
    <nav className={cn("flex items-center space-x-4", className)}>
      <a href="/" className={staticClass}>Home</a>
      <a href="/compro-oro" className={staticClass}>Compro Oro</a>
      <a href="/oro-da-investimento" className={staticClass}>Oro da Investimento</a>
      <a href="/diamanti" className={staticClass}>Diamanti</a>
      <a href="/grafici" className={staticClass}>Grafici</a>
      <a href="/avvisi" className={staticClass}>Avvisi</a>
      <a href="/assistente-ai" className={staticClass}>Assistente AI</a>
      <a href="/previsioni" className={staticClass}>Previsioni AI</a>
      <a href="/blog" className={staticClass}>Blog</a>
      {/* Admin link NOT rendered in SSR - isAuthenticated is always false during SSR */}
    </nav>
  );
}

/**
 * Full interactive navigation with Radix NavigationMenu dropdowns
 * Only rendered AFTER hydration is complete
 */
function InteractiveNavLinks({ 
  isAuthenticated, 
  userRole, 
  className, 
  onItemClick,
  isOroActive,
  isDiamantiActive
}: NavItemsProps & { isOroActive: boolean; isDiamantiActive: boolean }) {
  return (
    <nav className={cn("flex items-center space-x-4", className)}>
      <NavLink to="/" className={navLinkClass} onClick={onItemClick} end>
        Home
      </NavLink>
      <NavLink to="/compro-oro" className={navLinkClass} onClick={onItemClick}>
        Compro Oro
      </NavLink>
      
      <NavigationMenu>
        <NavigationMenuList>
          <NavigationMenuItem>
            <NavigationMenuTrigger className={cn("h-auto bg-transparent px-0 py-0 text-sm font-medium hover:bg-transparent focus:bg-transparent data-[state=open]:bg-transparent data-[active]:bg-transparent transition-colors", isOroActive ? "text-gold" : "hover:text-gold/80")}>
              Oro da Investimento
            </NavigationMenuTrigger>
            <NavigationMenuContent>
              <div className="w-[420px] bg-background border shadow-lg rounded-md overflow-hidden">
                {/* Hub Section - Featured */}
                <NavLink 
                  to="/oro-da-investimento"
                  onClick={onItemClick}
                  className="block p-5 bg-gradient-to-r from-amber-50 to-yellow-50 dark:from-amber-950/30 dark:to-yellow-950/30 border-b hover:from-amber-100 hover:to-yellow-100 dark:hover:from-amber-950/50 dark:hover:to-yellow-950/50 transition-colors"
                >
                  <div className="flex items-start gap-3">
                    <div className="p-2 bg-amber-500/10 rounded-lg">
                      <TrendingUp className="h-6 w-6 text-amber-600" />
                    </div>
                    <div className="flex-1">
                      <div className="text-base font-semibold text-foreground">
                        Oro da Investimento
                      </div>
                      <p className="text-sm text-muted-foreground mt-1 leading-relaxed">
                        Guida completa: confronto monete vs lingotti, vantaggi fiscali, come scegliere.
                      </p>
                      <span className="inline-flex items-center text-sm font-medium text-amber-600 dark:text-amber-500 mt-2">
                        Scopri la guida <ArrowRight className="ml-1 h-4 w-4" />
                      </span>
                    </div>
                  </div>
                </NavLink>
                
                {/* Subcategories Grid */}
                <div className="grid grid-cols-2 divide-x">
                  {/* Monete */}
                  <NavLink 
                    to="/monete-oro-investimento"
                    onClick={onItemClick}
                    className="p-4 hover:bg-muted/50 transition-colors"
                  >
                    <div className="flex items-center gap-2 mb-2">
                      <Coins className="h-5 w-5 text-amber-500" />
                      <span className="font-medium text-sm">Monete d'Oro</span>
                    </div>
                <p className="text-xs text-muted-foreground">
                  Sterlina, Krugerrand, Marengo e altre...
                </p>
                  </NavLink>
                  
                  {/* Lingotti */}
                  <NavLink 
                    to="/lingotti-oro-investimento"
                    onClick={onItemClick}
                    className="p-4 hover:bg-muted/50 transition-colors"
                  >
                    <div className="flex items-center gap-2 mb-2">
                      <Package className="h-5 w-5 text-amber-500" />
                      <span className="font-medium text-sm">Lingotti d'Oro</span>
                    </div>
                    <p className="text-xs text-muted-foreground">
                      Certificati LBMA da 1g a 1kg
                    </p>
                  </NavLink>
                </div>
              </div>
            </NavigationMenuContent>
          </NavigationMenuItem>
        </NavigationMenuList>
      </NavigationMenu>

      <NavigationMenu>
        <NavigationMenuList>
          <NavigationMenuItem>
            <NavigationMenuTrigger className={cn("h-auto bg-transparent px-0 py-0 text-sm font-medium hover:bg-transparent focus:bg-transparent data-[state=open]:bg-transparent data-[active]:bg-transparent transition-colors", isDiamantiActive ? "text-gold" : "hover:text-gold/80")}>
              Diamanti
            </NavigationMenuTrigger>
            <NavigationMenuContent>
              <div className="w-[480px] bg-background border shadow-lg rounded-md overflow-hidden">
                {/* Hub Section - Featured */}
          <NavLink 
            to="/diamanti"
            onClick={onItemClick}
            className="block p-5 bg-gradient-to-r from-gold-surface to-gold-muted border-b hover:from-gold-muted hover:to-gold-surface transition-colors"
          >
            <div className="flex items-start gap-3">
              <div className="p-2 bg-gold-surface rounded-lg border border-gold/20">
                <Gem className="h-6 w-6 text-gold" />
                    </div>
                    <div className="flex-1">
                      <div className="text-base font-semibold text-foreground">
                        Diamanti
                      </div>
                      <p className="text-sm text-muted-foreground mt-1 leading-relaxed">
                        Guida completa: le 4C, certificazioni, calcolo carati e quotazioni.
                      </p>
                <span className="inline-flex items-center text-sm font-medium text-gold dark:text-gold mt-2">
                  Scopri la guida <ArrowRight className="ml-1 h-4 w-4" />
                      </span>
                    </div>
                  </div>
                </NavLink>
                
                {/* Subcategories Grid - 3 columns */}
                <div className="grid grid-cols-3 divide-x">
            <NavLink 
              to="/quotazione-diamanti"
              onClick={onItemClick}
              className="p-4 hover:bg-gold-surface/50 transition-colors"
            >
              <div className="flex items-center gap-2 mb-2">
                <Gem className="h-5 w-5 text-gold" />
                <span className="font-medium text-sm">Quotazione</span>
              </div>
              <p className="text-xs text-muted-foreground">
                Calcola il valore del tuo diamante
              </p>
            </NavLink>
            
            <NavLink 
              to="/calcolatore-carati-diamante"
              onClick={onItemClick}
              className="p-4 hover:bg-gold-surface/50 transition-colors"
            >
              <div className="flex items-center gap-2 mb-2">
                <Calculator className="h-5 w-5 text-gold" />
                <span className="font-medium text-sm">Calcolatore</span>
              </div>
              <p className="text-xs text-muted-foreground">
                Stima peso da misure mm
              </p>
            </NavLink>
            
            <NavLink 
              to="/le-4c-del-diamante"
              onClick={onItemClick}
              className="p-4 hover:bg-gold-surface/50 transition-colors"
            >
              <div className="flex items-center gap-2 mb-2">
                <BookOpen className="h-5 w-5 text-gold" />
                <span className="font-medium text-sm">Le 4C</span>
              </div>
              <p className="text-xs text-muted-foreground">
                Guida alla valutazione
              </p>
            </NavLink>
                </div>
              </div>
            </NavigationMenuContent>
          </NavigationMenuItem>
        </NavigationMenuList>
      </NavigationMenu>

      <NavLink to="/grafici" className={navLinkClass} onClick={onItemClick}>
        Grafici
      </NavLink>
      <NavLink to="/avvisi" className={navLinkClass} onClick={onItemClick}>
        Avvisi
      </NavLink>
      <NavLink to="/assistente-ai" className={navLinkClass} onClick={onItemClick}>
        Assistente AI
      </NavLink>
      <NavLink to="/previsioni" className={navLinkClass} onClick={onItemClick}>
        Previsioni AI
      </NavLink>
      <NavLink to="/blog" className={navLinkClass} onClick={onItemClick}>
        Blog
      </NavLink>
      {isAuthenticated && (userRole === 'admin' || userRole === 'super_admin') && (
        <NavLink to="/admin" className={navLinkClass} onClick={onItemClick}>
          Admin
        </NavLink>
      )}
    </nav>
  );
}

/**
 * NavItems - SSR-safe navigation component
 * 
 * HYDRATION STRATEGY:
 * - SSR + First client render: Static links only (no Radix NavigationMenu)
 * - Post-hydration: Full interactive version with dropdown menus
 * 
 * This prevents React #418 mismatch errors caused by:
 * - Radix internal ID generation differences
 * - aria-controls attribute changes
 * - Viewport/indicator mount timing
 */
export function NavItems({ 
  isAuthenticated, 
  userRole, 
  className,
  onItemClick
}: NavItemsProps) {
  const location = useLocation();
  const isOroActive = oroRoutes.includes(location.pathname);
  const isDiamantiActive = diamantiRoutes.includes(location.pathname);
  const { isHydrated } = useHydrationSafe();

  // CRITICAL: Render static version during SSR and initial hydration
  // Switch to interactive version only after hydration gate passes
  if (!isHydrated) {
    return <StaticNavLinks className={className} />;
  }

  return (
    <InteractiveNavLinks
      isAuthenticated={isAuthenticated}
      userRole={userRole}
      className={className}
      onItemClick={onItemClick}
      isOroActive={isOroActive}
      isDiamantiActive={isDiamantiActive}
    />
  );
}
